import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { scopeHeaders } from '../utils/scopeHeaders';
import DriverOnlyNotice from './DriverOnlyNotice';
import { apiBase } from '../../utils/authHeaders';
import { 
  Truck, MapPin, Navigation, Phone, CheckCircle, 
  XCircle, Clock, DollarSign, ToggleLeft, ToggleRight
} from 'lucide-react';

const MobileDelivery = () => {
  const location = useLocation();
  const { user, token, isOnline } = useMobileAuth();
  const [isDriverOnline, setIsDriverOnline] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverOnly, setDriverOnly] = useState(false);
  const [earnings, setEarnings] = useState({ today: 0, week: 0 });

  const [showProofModal, setShowProofModal] = useState(false);
  const [deliveryProof, setDeliveryProof] = useState({ photo: null, signature: null });

  // Load driver status, orders, and real earnings from the API.
  useEffect(() => {
    // Orders first: a 403 means this account is not a driver here, so skip the other driver calls.
    fetchOrders().then((isDriver) => {
      if (isDriver) { fetchDriverStatus(); fetchEarnings(); }
    });

    if (location.state?.action === 'start') {
        setIsDriverOnline(true);
    }
  }, [location.state]);

  const fetchDriverStatus = async () => {
    try {
      
      const response = await fetch(`${apiBase()}/api/mobile/driver/status`, {
        headers: scopeHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setIsDriverOnline(data.online);
      }
    } catch (error) {
      console.error('Error fetching driver status:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      
      // Fetch available orders + active orders
      const [availableRes, activeRes] = await Promise.all([
        fetch(`${apiBase()}/api/mobile/delivery/orders?status=available`, {
            headers: scopeHeaders(token)
        }),
        fetch(`${apiBase()}/api/mobile/delivery/orders?status=active`, {
            headers: scopeHeaders(token)
        })
      ]);
      if (availableRes.status === 403 || activeRes.status === 403) {
        setDriverOnly(true);
        setLoading(false);
        return false;
      }

      if (availableRes.ok) {
        const available = await availableRes.json();
        setAvailableOrders(available);
      }
      
      if (activeRes.ok) {
        const active = await activeRes.json();
        if (active.length > 0) {
            setActiveOrder(active[0]); // Assume one active order at a time for now
        }
      }
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
      return false;
    }
  };

  const fetchEarnings = async () => {
    try {
      
      const response = await fetch(`${apiBase()}/api/mobile/driver/earnings`, {
        headers: scopeHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setEarnings({ today: data.today || 0, week: data.week || 0 });
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const toggleStatus = async () => {
    const newStatus = !isDriverOnline;
    try {
        
        const response = await fetch(`${apiBase()}/api/mobile/driver/status`, {
            method: 'POST',
            headers: {
                ...scopeHeaders(token),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ online: newStatus })
        });
        
        if (response.ok) {
            setIsDriverOnline(newStatus);
            if (newStatus) fetchOrders();
        }
    } catch (error) {
        console.error('Error updating driver status:', error);
    }
  };

  const acceptOrder = async (order) => {
    try {
        
        const response = await fetch(`${apiBase()}/api/mobile/delivery/accept`, {
            method: 'POST',
            headers: {
                ...scopeHeaders(token),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ orderId: order.id })
        });

        if (response.ok) {
            setActiveOrder({ ...order, status: 'accepted' });
            setAvailableOrders(availableOrders.filter(o => o.id !== order.id));
        }
    } catch (error) {
        console.error('Error accepting order:', error);
    }
  };

  const updateOrderStatus = async (status) => {
    if (!activeOrder) return;
    
    try {
        
        const response = await fetch(`${apiBase()}/api/mobile/delivery/status`, {
            method: 'POST',
            headers: {
                ...scopeHeaders(token),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ orderId: activeOrder.id, status })
        });

        if (response.ok) {
            const updatedOrder = { ...activeOrder, status };
            setActiveOrder(updatedOrder);
            
            if (status === 'delivered') {
              setTimeout(() => {
                setActiveOrder(null);
                setEarnings(prev => ({ ...prev, today: prev.today + activeOrder.fee }));
                fetchOrders(); // Refresh list
              }, 2000);
            }
        }
    } catch (error) {
        console.error('Error updating order status:', error);
    }
  };

  const openNavigation = (address) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const handleCompleteDelivery = () => {
    setShowProofModal(true);
  };

  const submitDelivery = async () => {
    // In real app: Upload photo/signature first
    await updateOrderStatus('delivered');
    setShowProofModal(false);
    setDeliveryProof({ photo: null, signature: null });
  };

  if (driverOnly) return <DriverOnlyNotice />;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-muted">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted pb-20">
      {/* Header */}
      <div className="bg-surface shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3 flex justify-between items-center">
          <div>
            <h1 className="font-display text-xl font-bold text-ink">Driver Dashboard</h1>
            <p className="text-sm text-ink-muted">{isOnline ? 'Online' : 'Offline Mode'}</p>
          </div>
          <button 
            onClick={toggleStatus}
            className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
              isDriverOnline ? 'bg-primary-100 text-primary-700' : 'bg-surface-sunken text-ink-muted'
            }`}
          >
            {isDriverOnline ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
            <span className="font-medium">{isDriverOnline ? 'On Duty' : 'Off Duty'}</span>
          </button>
        </div>
        
        {/* Earnings Summary */}
        <div className="px-4 py-2 bg-primary-50 border-t border-primary-100 flex justify-around">
          <div className="text-center">
            <p className="text-xs text-primary-600 font-medium">Today</p>
            <p className="font-display text-lg font-bold text-primary-900">£{earnings.today.toFixed(2)}</p>
          </div>
          <div className="text-center border-l border-primary-200 pl-4">
            <p className="text-xs text-primary-600 font-medium">This Week</p>
            <p className="font-display text-lg font-bold text-primary-900">£{earnings.week.toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Active Order View */}
        {activeOrder ? (
          <div className="bg-surface rounded-xl shadow-lg border-2 border-primary-100 overflow-hidden">
            <div className="bg-primary-500 px-4 py-3 text-white flex justify-between items-center">
              <span className="font-bold flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                In Progress
              </span>
              <span className="bg-primary-500 px-2 py-0.5 rounded text-xs">#{activeOrder.id}</span>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Pickup / Dropoff Steps */}
              <div className="space-y-6 relative">
                {/* Connecting Line */}
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-surface-border" />

                {/* Pickup */}
                <div className="relative pl-8">
                  <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center ${
                    ['picked_up', 'delivered'].includes(activeOrder.status) ? 'bg-primary-100 text-primary-600' : 'bg-primary-100 text-primary-600'
                  } border-2 border-white shadow-sm`}>
                    <MapPin className="w-3 h-3" />
                  </div>
                  <div>
                    <p className="text-xs text-ink-muted uppercase font-semibold">Pickup</p>
                    <h3 className="font-bold text-ink">{activeOrder.restaurant}</h3>
                    <div className="mt-2 space-y-1">
                      {activeOrder.items.map((item, idx) => (
                        <p key={idx} className="text-sm text-ink-muted">• {item}</p>
                      ))}
                    </div>
                    {activeOrder.status === 'accepted' && (
                      <button 
                        onClick={() => updateOrderStatus('picked_up')}
                        className="mt-3 w-full py-2 bg-primary-500 text-white rounded-lg font-medium text-sm"
                      >
                        Confirm Pickup
                      </button>
                    )}
                  </div>
                </div>

                {/* Dropoff */}
                <div className="relative pl-8">
                  <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center ${
                    activeOrder.status === 'delivered' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
                  } border-2 border-white shadow-sm`}>
                    <MapPin className="w-3 h-3" />
                  </div>
                  <div>
                    <p className="text-xs text-ink-muted uppercase font-semibold">Dropoff</p>
                    <h3 className="font-bold text-ink">{activeOrder.customerName}</h3>
                    <p className="text-sm text-ink-muted">{activeOrder.address}</p>
                    
                    <div className="flex space-x-2 mt-3">
                      <button 
                        onClick={() => openNavigation(activeOrder.address)}
                        className="flex-1 flex items-center justify-center py-2 border border-surface-border rounded-lg text-sm font-medium text-ink"
                      >
                        <Navigation className="w-4 h-4 mr-1" />
                        Navigate
                      </button>
                      <button className="flex-1 flex items-center justify-center py-2 border border-surface-border rounded-lg text-sm font-medium text-ink">
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </button>
                    </div>

                    {activeOrder.status === 'picked_up' && (
                      <button 
                        onClick={handleCompleteDelivery}
                        className="mt-3 w-full py-3 bg-primary-500 text-white rounded-lg font-bold shadow-md"
                      >
                        Complete Delivery
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Available Orders List */
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold text-ink">Available Orders</h2>
              <span className="text-sm text-ink-muted">{availableOrders.length} nearby</span>
            </div>

            {isDriverOnline ? (
              availableOrders.map(order => (
                <div key={order.id} className="bg-surface rounded-xl shadow-sm p-4 border border-surface-border">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-bold text-ink">{order.restaurant}</h3>
                      <div className="flex items-center text-sm text-ink-muted mt-1">
                        <Navigation className="w-3 h-3 mr-1" />
                        <span>{order.distance}</span>
                        <span className="mx-2">•</span>
                        <span>{order.items.length} items</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="block font-display text-lg font-bold text-primary-600">£{Number(order.fee || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="bg-surface-muted rounded-lg p-3 mb-3">
                    <div className="flex items-start space-x-2">
                      <MapPin className="w-4 h-4 text-ink-subtle mt-0.5" />
                      <div>
                        <p className="text-xs text-ink-muted uppercase font-semibold">Deliver to</p>
                        <p className="text-sm font-medium text-ink">{order.address}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button 
                      onClick={() => {/* Decline logic */}}
                      className="flex-1 py-2 text-danger bg-danger/5 rounded-lg font-medium text-sm"
                    >
                      Decline
                    </button>
                    <button 
                      onClick={() => acceptOrder(order)}
                      className="flex-[2] py-2 bg-primary-500 text-white rounded-lg font-medium text-sm"
                    >
                      Accept Order
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-surface rounded-xl border-dashed border-2 border-surface-border">
                <Truck className="w-12 h-12 text-ink-subtle mx-auto mb-3" />
                <h3 className="text-lg font-medium text-ink">You are offline</h3>
                <p className="text-ink-muted text-sm mb-4">Go online to start receiving orders</p>
                <button 
                  onClick={toggleStatus}
                  className="px-6 py-2 bg-primary-500 text-white rounded-full font-medium"
                >
                  Go Online
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Proof of Delivery Modal */}
      {showProofModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl w-full max-w-sm overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-surface-border flex justify-between items-center">
              <h3 className="font-bold text-ink">Proof of Delivery</h3>
              <button onClick={() => setShowProofModal(false)}><XCircle className="w-6 h-6 text-ink-subtle" /></button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto">
              <div className="space-y-2">
                <label className="text-sm font-medium text-ink">Take Photo</label>
                <div className="border-2 border-dashed border-surface-border rounded-lg p-8 text-center bg-surface-muted hover:bg-surface-sunken cursor-pointer">
                  <div className="mx-auto w-12 h-12 bg-surface rounded-full flex items-center justify-center shadow-sm mb-2">
                    <span className="text-2xl">📸</span>
                  </div>
                  <p className="text-sm text-ink-muted">Tap to capture photo</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-ink">Signature</label>
                <div className="border border-surface-border rounded-lg h-32 bg-surface-muted flex items-center justify-center">
                  <p className="text-ink-subtle text-sm">Sign here</p>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-surface-border">
              <button 
                onClick={submitDelivery}
                className="w-full py-3 bg-primary-500 text-white rounded-lg font-bold shadow-md flex items-center justify-center"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Confirm Delivery
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileDelivery;
